/* Progressive enhancement: the image remains visible until both model and texture are ready. */
const reduced = matchMedia('(prefers-reduced-motion: reduce)');
let libraries;
function dependencies() {
  return libraries ||= Promise.all([import('three'), import('three/addons/loaders/GLTFLoader.js'), import('three/addons/loaders/DRACOLoader.js'), import('three/addons/controls/OrbitControls.js')]).catch(error => { libraries = null; throw error; });
}
function init(root) {
  if (root.dataset.initialized) return;
  root.dataset.initialized = 'true';
  const hero = root.dataset.spatial === 'hero';
  const host = root.querySelector('[data-canvas]');
  const toggle = root.querySelector('[data-spatial-toggle]');
  const motion = root.querySelector('[data-motion]');
  const status = root.querySelector('[data-spatial-status]');
  let active = false, visible = false, paused = true, busy = false, disposed = false, state, frame = 0;
  function paint() { if (state && active) state.renderer.render(state.scene, state.camera); }
  function stop() { cancelAnimationFrame(frame); frame = 0; }
  function animate(time) {
    if (!active || !visible || document.hidden || paused || reduced.matches || !hero) { stop(); return; }
    state.model.rotation.y = -.25 + Math.sin(time / 8500) * .16;
    state.model.position.y = Math.sin(time / 5500) * .018;
    paint(); frame = requestAnimationFrame(animate);
  }
  function run() { stop(); if (state && hero && active && visible && !paused && !reduced.matches && !document.hidden) frame = requestAnimationFrame(animate); }
  function show(on) {
    active = on;
    root.classList.toggle('is-3d', on);
    toggle.textContent = on ? toggle.dataset.still : toggle.dataset.load;
    toggle.setAttribute('aria-pressed', String(on));
    host.tabIndex = on ? 0 : -1;
    if (motion) { motion.hidden = !on || reduced.matches; motion.textContent = paused ? motion.dataset.play : motion.dataset.pause; }
    paint(); run();
  }
  function resize() {
    if (!state) return;
    const {width, height} = host.getBoundingClientRect();
    if (!width || !height) return;
    state.renderer.setSize(width, height, false);
    state.camera.aspect = width / height; state.camera.updateProjectionMatrix(); paint();
  }
  function updateProduct() {
    if (!state || hero) return;
    const product = root.closest('[data-product]');
    const data = JSON.parse(product.querySelector('[data-product-json]').textContent);
    const id = product.querySelector('[data-variant-id]').value;
    const variant = data.variants.find(v => String(v.id) === id);
    const options = [...product.querySelectorAll('[data-opt].is-active')].map(b=>b.dataset.opt);
    if (!options.length) options.push(...(variant?.options || []));
    const size = options.join(' ').match(/(\d+)\s*[x×X*]\s*(\d+)/);
    const ratio = Number(root.dataset.ratio) || .7;
    const width = size ? Math.min(1.5, Number(size[1])/100) : .7;
    const height = size ? Math.min(1.8, Number(size[2])/100) : width/ratio;
    const framed = /ממוסגר|framed/i.test(options.join(' '));
    const digital = /קובץ|הורדה|digital|download/i.test(options.join(' '));
    state.artGroup.scale.set(width,height,1);
    const surface = state.artGroup.children.find(o=>o.name==='Artwork');
    const texture = surface.material.map;
    const physicalRatio = width/height;
    texture.repeat.set(Math.min(1,physicalRatio/ratio),Math.min(1,ratio/physicalRatio));
    texture.offset.set((1-texture.repeat.x)/2,(1-texture.repeat.y)/2);
    state.artGroup.visible = !digital;
    status.textContent = digital ? root.dataset.digital : '';
    state.artGroup.children.forEach(o => { if (o.name.startsWith('Frame')) o.visible = framed; });
    paint();
  }
  function fail() {
    show(false); status.textContent = root.dataset.error;
    if (state) { state.renderer.dispose(); state.controls.dispose(); host.replaceChildren(); state = null; }
  }
  async function load() {
    if (state) { show(!active); return; }
    if (busy || disposed) return;
    busy = true; toggle.disabled = true; root.setAttribute('aria-busy','true'); status.textContent = '';
    let renderer, draco;
    try {
      const [THREE, {GLTFLoader}, {DRACOLoader}, {OrbitControls}] = await dependencies();
      if (disposed) return;
      renderer = new THREE.WebGLRenderer({alpha:true, antialias:true, powerPreference:'low-power'});
      renderer.setPixelRatio(Math.min(devicePixelRatio,1.5)); renderer.outputColorSpace = THREE.SRGBColorSpace;
      renderer.toneMapping = THREE.ACESFilmicToneMapping; renderer.toneMappingExposure = hero ? .92 : .95;
      renderer.shadowMap.enabled = true; renderer.shadowMap.type = THREE.PCFSoftShadowMap;
      draco = new DRACOLoader().setDecoderPath('https://cdn.jsdelivr.net/npm/three@0.180.0/examples/jsm/libs/draco/gltf/');
      const loader = new GLTFLoader().setDRACOLoader(draco);
      const [gltf, texture] = await Promise.all([loader.loadAsync(root.dataset.model), new THREE.TextureLoader().loadAsync(root.dataset.texture)]);
      if (disposed) { renderer.dispose(); texture.dispose(); return; }
      texture.colorSpace = THREE.SRGBColorSpace; texture.flipY = false;
      if (root.dataset.crop) { texture.repeat.set(.704,.675); texture.offset.set(.158,.099); }
      const scene = new THREE.Scene();
      const camera = new THREE.PerspectiveCamera(hero ? 34 : 38,1,.01,30);
      camera.position.set(0,hero ? 0 : 1.3,hero ? 3.05 : 2.75);
      const target = new THREE.Vector3(0,hero ? 0 : 1.3,0);
      const controls = new OrbitControls(camera, host); controls.target.copy(target);
      controls.enablePan=false; controls.enableZoom=false; controls.enableDamping=false;
      controls.minAzimuthAngle=-.55; controls.maxAzimuthAngle=.55;
      controls.minPolarAngle=Math.PI*.38; controls.maxPolarAngle=Math.PI*.56;
      controls.update(); controls.addEventListener('change',paint);
      host.replaceChildren(renderer.domElement);
      const model=gltf.scene; scene.add(model);
      const artGroup=new THREE.Group();
      // The canvas back sits 2.5 mm off the wall, rather than floating in front of it.
      if (!hero) { scene.add(artGroup); artGroup.position.set(0,1.65,-.04); }
      model.traverse(o => {
        if (o.isMesh) { o.castShadow=true; o.receiveShadow=true; }
        // The print already contains its relief-look light and shade. Preserve those
        // tones instead of compressing them again through the scene's tone mapper.
        if (o.name === 'Artwork') { o.material = new THREE.MeshBasicMaterial({map:texture,color:hero ? 0xf4ede2 : 0xe8e2d6,toneMapped:false}); }
      });
      if (hero) {
        model.scale.set(Number(root.dataset.ratio) || 1.86,1,1); model.children.forEach(o => { if(o.name.startsWith('Frame')) o.visible=false; });
      } else {
        [...model.children].forEach(o => { if(o.name==='Artwork'||o.name==='Canvas'||o.name.startsWith('Frame')) artGroup.add(o); });
      }
      scene.add(new THREE.HemisphereLight(0xfffbef,0x777069,hero ? 2 : 1.25));
      const key=new THREE.DirectionalLight(0xfff3dc,hero ? 2.8 : 1.6); key.position.set(-3,5,4); key.castShadow=true; key.shadow.mapSize.set(1024,1024); key.shadow.normalBias=.002;
      Object.assign(key.shadow.camera,{left:-4,right:4,top:4,bottom:-4,near:.1,far:15}); scene.add(key);
      state={renderer,scene,camera,controls,model,artGroup};
      renderer.domElement.addEventListener('webglcontextlost',e=>{e.preventDefault();fail();},{once:true});
      updateProduct(); resize(); show(true); paint();
    } catch (error) { renderer?.dispose(); fail(); }
    finally { draco?.dispose(); busy=false; toggle.disabled=false; root.removeAttribute('aria-busy'); }
  }
  toggle.addEventListener('click',load);
  motion?.addEventListener('click',()=>{paused=!paused;show(active);});
  host.addEventListener('pointerdown',()=>{paused=true;if(motion)motion.textContent=motion.dataset.play;stop();});
  host.addEventListener('keydown',event=>{
    if (!state || !['ArrowLeft','ArrowRight'].includes(event.key)) return;
    event.preventDefault(); paused=true; stop();
    const offset=state.camera.position.clone().sub(state.controls.target);
    const angle=Math.atan2(offset.x,offset.z)+(event.key==='ArrowLeft'?-.08:.08);
    const limited=Math.max(-.55,Math.min(.55,angle));
    const radius=Math.hypot(offset.x,offset.z);
    state.camera.position.x=state.controls.target.x+Math.sin(limited)*radius;
    state.camera.position.z=state.controls.target.z+Math.cos(limited)*radius;
    state.controls.update(); if(motion)motion.textContent=motion.dataset.play; paint();
  });
  const onProductClick=e=>{if(e.target.closest('[data-opt]'))updateProduct();};
  root.closest('[data-product]')?.addEventListener('click',onProductClick);
  const onVisibility=()=>run(); document.addEventListener('visibilitychange',onVisibility);
  const onReduced=()=>{if(reduced.matches) {paused=true;if(state)state.model.rotation.y=0;}show(active);};
  reduced.addEventListener('change',onReduced);
  const observer=new IntersectionObserver(entries=>{
    visible=entries[0].isIntersecting;
    if(root.dataset.autoload === 'true' && visible && !state && !busy && !reduced.matches && !navigator.connection?.saveData) { observerAuto(); }
    run();
  },{threshold:.1});
  let autoAttempted=false;
  function observerAuto(){if(!autoAttempted){autoAttempted=true;load();}}
  observer.observe(root);
  const sizing=new ResizeObserver(resize); sizing.observe(host);
  host.tabIndex=-1;
  root.cleanup=()=>{
    disposed=true; stop(); observer.disconnect(); sizing.disconnect();
    document.removeEventListener('visibilitychange',onVisibility); reduced.removeEventListener('change',onReduced);
    root.closest('[data-product]')?.removeEventListener('click',onProductClick);
    state?.controls.dispose(); state?.renderer.dispose();
    state?.scene.traverse(o=>{o.geometry?.dispose();if(o.material){o.material.map?.dispose();o.material.dispose();}});
  };
}
function boot(scope=document){scope.querySelectorAll('[data-spatial]').forEach(init);}
boot();
document.addEventListener('shopify:section:load',e=>boot(e.target));
document.addEventListener('shopify:section:unload',e=>e.target.querySelectorAll('[data-spatial]').forEach(r=>r.cleanup?.()));
